import { useState, useCallback } from "react";

import { toast } from "@/components/ui/sonner";
import {
  invokeAuthenticatedEdgeFunction,
  invokePublicEdgeFunction,
  EdgeFunctionOptions,
  EdgeFunctionResult,
} from "@/lib/edgeFunctionHelpers";

interface UseEdgeFunctionOptions {
  auth?: boolean;
}

interface ExecuteOptions extends EdgeFunctionOptions {
  showErrorToast?: boolean;
  showSuccessToast?: boolean;
  successMessage?: string;
}

/**
 * A unified hook for invoking Supabase edge functions with consistent state management,
 * error handling, and authentication logic.
 *
 * @param options - Configuration for the hook, including authentication requirement.
 *
 * @example
 * // For authenticated functions
 * const { execute, isLoading } = useEdgeFunction({ auth: true });
 * const result = await execute('my-secure-function', { body: { ... } });
 *
 * @example
 * // For public functions
 * const { execute, isLoading } = useEdgeFunction({ auth: false });
 * const result = await execute('my-public-function', { body: { ... } });
 */
export const useEdgeFunction = <TResponse = any>(
  options: UseEdgeFunctionOptions = { auth: true }
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<TResponse | null>(null);

  const execute = useCallback(
    async (
      functionName: string,
      edgeFunctionOptions?: ExecuteOptions
    ): Promise<{ data: TResponse | null; error: Error | null }> => {
      setIsLoading(true);
      setError(null);

      try {
        const invokeFunction = options.auth
          ? invokeAuthenticatedEdgeFunction
          : invokePublicEdgeFunction;

        const result = await invokeFunction<TResponse>(
          functionName,
          edgeFunctionOptions
        );

        if (result.error) {
          throw result.error;
        }

        setData(result.data);

        if (edgeFunctionOptions?.showSuccessToast) {
          toast.success(edgeFunctionOptions.successMessage || "Operation completed successfully");
        }

        return { data: result.data, error: null };
      } catch (err: any) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);

        if (edgeFunctionOptions?.showErrorToast) {
          toast.error(error.message || "An error occurred");
        }

        return { data: null, error };
      } finally {
        setIsLoading(false);
      }
    },
    [options.auth]
  );

  return {
    execute,
    isLoading,
    error,
    data,
  };
};
